/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowCircleLeft, ArrowCircleRight, Delete } from "@mui/icons-material";
import { Tab, Tabs, Button, IconButton, Stack } from "@mui/material";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import isEqual from "react-fast-compare";
import { indexOf } from "lodash";
import { Logger } from "cms/utils/log/logger";
import { isArrayNotEmpty, isArray } from "utils/empty-exists";
import { defaultRichTextObjectPM } from "cms/components/components/defaults";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";

import { TabContent } from "./tab_content";
// TODO: why are we using module css here? (makes it not visible in App.css)
import styles from "cms/components/components/tab-group/edit/index.module.css";

const TabGroupComponentEditor = () => {
    
    const { component, update } = useUpdateComponent();
    const _tabs = component?.tabs;
    // console.log('[TabGroupEditor] load', _tabs)
    
    const { t } = useTranslation();
    const itemTabs = isArray(_tabs) ? _tabs : [];
    const firstTabGuid = isArrayNotEmpty(_tabs) ? _tabs[0]?.guid : undefined;
    const defaultTabs = new Map([...itemTabs].map((tab) => [tab.guid, tab]));
    
    const [tabs, setTabs] = useState(defaultTabs);
    const [selectedTab, setSelectedTab] = useState(firstTabGuid);
    // console.log('[TabGroupEditor] firstTabGuid', firstTabGuid)
    
    /**
     * Set a Selected tab and update form fields to use the data of that tab.
     * @param event
     * @param guid
     */
    function onSelectTab (event, guid) {
        setSelectedTab(guid);
        if (isArrayNotEmpty(_tabs)) {
            const tab = _tabs.find((tab) => tab.guid === guid);
            const { title, richtext = defaultRichTextObjectPM } = tab || {};
            const count = richtext?.content?.length || 0;
            Logger.debug(`${guid} - ${count} - ${title}`, richtext);
            setSelectedTab(guid);
        }
    }
    
    /**
     * Add a new tab to the map of tabs.
     */
    const addNewTab = () => {
        let updatedTabs = new Map(tabs);
        const guid = uuidv4();
        setSelectedTab(guid);
        updatedTabs.set(guid, { title: "[New]", guid, richtext: defaultRichTextObjectPM });
        setTabs(updatedTabs);
        const tabsArray = Array.from(updatedTabs.values());
        update("tabs", tabsArray);
    };
    
    /**
     * Remove the tab from the map and set the selected tab to the first tab. Might make sense to go to
     * previous tab but this is the quick way for now.
     *
     * @param guid
     */
    function removeTab (guid) {
        const updatedTabs = new Map(tabs);
        const originalKeys = [...tabs.keys()];
        const guidKeyIdx = indexOf(originalKeys, guid);
        const isFirstTab = guidKeyIdx > 0;
        // If there is a tab before the deleted one move to that, otherwise move to the next tab
        const selectedGuid = isFirstTab
            ? originalKeys[guidKeyIdx - 1]
            : originalKeys[guidKeyIdx + 1];
        onSelectTab(null, selectedGuid);
        updatedTabs.delete(guid);
        setTabs(updatedTabs);
        const tabsArray = Array.from(updatedTabs.values());
        update("tabs", tabsArray);
    }

    function updateTab (guid, prevTab, nextTab) {
        if (isEqual(prevTab, nextTab)) {
            //
        } else {
            const updatedTabs = new Map(tabs);
            updatedTabs.set(guid, nextTab);
            setTabs(updatedTabs);
            const tabsArray = Array.from(updatedTabs.values());
            update("tabs", tabsArray);
        }
    }
    
    /**
     * This method re-orders the keys in the map of tabs this is used to re-order when a user clicks right or left.
     *
     * @param guid
     * @param increment
     * @returns {boolean}
     * @private
     */
    function _shiftTab (guid, increment) {
        const keys = [...tabs.keys()];
        const guidIdx = indexOf(keys, guid);
        const newPosition = guidIdx + increment;
        // Logger.debug(`newPosition ${newPosition}`)
        
        let swappedKeys = [...keys];
        
        // return false if moving past the beginning or end of array
        if (newPosition < 0 || newPosition > swappedKeys.length - 1) {
            Logger.debug(`position outside of range ${newPosition}, range 0 - ${swappedKeys.length - 1}`);
            return false;
        }
        swappedKeys[newPosition] = keys[guidIdx];
        swappedKeys[guidIdx] = keys[newPosition];
        swappedKeys = swappedKeys.filter(function ( element ) {
            return element !== undefined;
        });
        const map = new Map();
        swappedKeys.reduce((acc, key) => {
            acc[key] = tabs.get(key);
            map.set(key, tabs.get(key));
            return acc;
        }, {});
        setTabs(map);
        const tabsArray = Array.from(map.values());
        update("tabs", tabsArray);
    }

    function moveTabLeft (guid) {
        // console.debug(`moveTabLeft -1`)
        _shiftTab(guid, -1);
    }

    function moveTabRight (guid) {
        // console.debug(`moveTabRight +1`)
        _shiftTab(guid, 1);
    }
    
    /**
     * Render the buttons to delete and move the tab right and left.
     *
     * @param guid
     * @returns {unknown[]}
     */
    function getEditorIconButtons (guid) {
        const iconButtons = [
            { fn: removeTab, ariaLabel: "delete", icon: <Delete /> },
            { fn: moveTabLeft, ariaLabel: "move left", icon: <ArrowCircleLeft /> },
            { fn: moveTabRight, ariaLabel: "move right", icon: <ArrowCircleRight /> },
        ];
        
        return iconButtons.map((button, idx) => {
            return (
                <IconButton
                    key={idx}
                    aria-label={button.ariaLabel}
                    onClick={() => {
                        button.fn(guid);
                    }}
                >
                    {button.icon}
                </IconButton>
            );
        });
    }
    

    
    return (
        <div className="simplicity-editor-frame">
            {!tabs && <>{t("Components.editThisItemToAddSomeTabs")}</>}
            {tabs && (
                <div className="panel-group tabs" data-group-type="tabs" style={styles.tabs}>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                        <Button
                            className="accordion-component-add-section-button"
                            size="medium"
                            startIcon={<AddCircleOutlineIcon/>}
                            color={"primary"}
                            target="warning"
                            onClick={addNewTab}
                        >
                            Add Tab
                        </Button>
                    </div>
                    
                    {/* This renders the tab headings */}
                    <Tabs
                        value={selectedTab}
                        onChange={onSelectTab}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {tabs &&
                            [...tabs.keys()].map((guid, index) => {
                                // console.debug('tabs',tabs)
                                const tab = tabs?.get(guid);
                                const key = `${guid}${index}`;
                                return (
                                    <Tab
                                        key={key}
                                        value={guid}
                                        label={tab?.title}
                                    />
                                );
                            })
                        }
                    </Tabs>
                    
                    <Stack direction="column" justifyContent="flex-start" mb={4}>
                        <Stack direction="row" justifyContent="flex-end">
                            {getEditorIconButtons(selectedTab)}
                        </Stack>
                        
                        {
                            [...tabs.keys()].map((id, index) => {
                                const tab = tabs?.get(id);
                                const { guid } = tab || {};
                                const key = index + guid;
                                const isCurrent = selectedTab === guid;
                                return (
                                    <TabContent
                                        onClick={(event) => onSelectTab(event, guid)}
                                        key={key}
                                        isCurrent={isCurrent}
                                        tab={tab}
                                        updateTab={updateTab}
                                    />
                                );
                            })}
                    </Stack>
                </div>
            )}
        </div>
    );
};

export { TabGroupComponentEditor };
